import { get, retry } from "@/utils/axios";
import { URL } from "@/utils/url";
import { sortContacts } from "@/utils/contactSorting";
import {
  sortByDate,
  sortByAlphabetOrder,
  generateRandomChar,
  getMonthName,
  numberFormat,
  getConditionalStatusWithPartnerStatus,
  getMonthByName,
} from "@/utils/common";

import {
  investmentAppetiteItemType,
  investmentAppetiteType,
  fundCommitmentType,
  FlexibleTableProp,
  instituteProps,
  jsonFilePath,
  partnerStatuses,
  isBrandToSkip,
} from "@/common";
import { sliceRelatedNewsPage } from "@/utils/relatedNews";

const fs = require("fs");
const maxNumberOfRecords = 5000;
const pageNumber = 1;

const {
  routes: { relatedNews, contactInfo, fundCommitment, fundManaged, blaize },
  commonRoute,
} = URL;

// Sitemap-membership probe: a non-200 here drops the profile from the sitemap
// (utils/common.ts generateSiteMap). It deliberately asks the LEGACY host rather
// than the navigation host — the page it checks for is the old WordPress profile,
// and CP-150 moved this build-time probe off the www host after per-profile
// requests drove Lambda@Edge invocations up. WEB-33210 moved navigation to the
// current public domains but left this probe where CP-150 put it, so build
// traffic is unchanged.
export const pingOldProfile = async (institution: string) => {
  try {
    const url = `\\institution-profiles\\${institution.formatString()}.html`;
    const response = await get(url, URL.legacyProfileProbeURL);
    if (response.status !== 200) {
      return false;
    }
    return true;
  } catch (error) {
    return false;
  }
};

// The on-profile Related News block asks RA API for each relation separately
// (WEB-32715). Previously it pulled one fixed page of the newest 20 associations
// and split them locally, which starved About on any firm where Mentions dominate
// recent tagging — Blackstone/PEI rendered 2 of 9 available About articles because
// 18 of the newest 20 records were Mentions.
//
// Two targeted requests cost exactly two round trips for every firm, however deep
// its history. The earlier fix for this paged forward until both sections filled,
// which could not terminate early for a firm with fewer than 9 About articles —
// the common case, since editors tag About sparingly — and read that firm's entire
// history instead.
//
// `relatedToRelation` is OPTIONAL on RA API v1 and partitions on NOT-mention for
// `about`, so associations whose relation is null or '' still count as About
// exactly as they did when this was bucketed client-side.
const ABOUT_LIMIT = 9; // 1 for the overview panel + up to 8 for Stories About section
const MENTION_LIMIT = 8;
// Over-fetch mentions by the About cap so the section still fills after any
// article that is BOTH about and a mention of this institution is dropped.
const MENTION_FETCH_SIZE = MENTION_LIMIT + ABOUT_LIMIT;

const fetchRelatedNewsByRelation = async (
  institutionId: string,
  relation: "about" | "mention",
  size: number
): Promise<{ records: any[]; total: number }> => {
  const url = `${relatedNews}?page=1&size=${size}&relatedToInstitution=${institutionId}&order=createdDate,desc&relatedToRelation=${relation}`;
  const response = await retry(
    async () => await get(url),
    `${institutionId}/headlines-${relation}`
  );
  const records: any[] = [...(response.data.records ?? [])].sort((x: any, y: any) =>
    sortByDate(x, y, "createdDate", "dsc")
  );
  return { records, total: Number(response.data.pagination?.total) || 0 };
};

export const getHeadLines = async (institutionId: string) => {
  try {
    // In parallel: the two requests are independent, so this costs one round trip
    // of latency rather than two.
    const [about, mention] = await Promise.all([
      fetchRelatedNewsByRelation(institutionId, "about", ABOUT_LIMIT),
      fetchRelatedNewsByRelation(institutionId, "mention", MENTION_FETCH_SIZE),
    ]);

    const aboutItems = about.records;

    // An article carrying two relatedTo entries for the SAME institution — one
    // about, one mention — satisfies both relation queries, so it arrives in both
    // lists. About wins the tie, and dropping it from Mentions would leave that
    // section a record short even though more mentions exist deeper.
    //
    // Hence MENTION_FETCH_SIZE above: at most ABOUT_LIMIT ids can be removed here,
    // so fetching MENTION_LIMIT + ABOUT_LIMIT guarantees MENTION_LIMIT survivors
    // whenever that many exist. Costs a slightly larger response, not a request.
    const aboutIds = new Set<string>(aboutItems.map((record: any) => record.id));
    const mentionItems = mention.records
      .filter((record: any) => !aboutIds.has(record.id))
      .slice(0, MENTION_LIMIT);

    // The two relation queries partition the same hits the single unfiltered query
    // used to count, so the sum carries the meaning totalCount always had. It stays
    // load-bearing: getBuildStatus reads it to decide whether the profile page is
    // emitted at all and whether the slug stays in the sitemap.
    const totalCount = about.total + mention.total;

    return { totalCount, aboutItems, mentionItems };
  } catch (ex: any) {
    console.error(`getHeadLines: institutionId: ${institutionId}, error`, {
      time: new Date().toISOString(),
      url: ex?.config?.url,
      params: ex?.config?.params,
      message: ex?.message,
      code: ex?.code,
      status: ex?.response?.status,
      data: ex?.response?.data,
      stack: ex?.stack,
    });
    return { totalCount: 0, aboutItems: [], mentionItems: [] };
  }
};

// Related News listing pages (WEB-27455). RA API filters cross-published records out
// of each page AFTER OpenSearch counted the hits, so pagination.total over-reports
// and its page windows come back short — Blackstone/PERE reports total 18 while only
// 15 articles render, giving pages of 7 + 8 instead of 10 + 5. content-hub therefore
// walks the raw pages itself and treats what came back as the truth.
//
// The cost is bounded by walking in big batches, not in display-sized pages: the
// worst institution on stage has 107 articles, so at a batch of 1000 every firm is
// ONE request and the build's request count is unchanged. An earlier version of this
// idea walked in 10s with no concurrency limit and blew Next's
// staticPageGenerationTimeout on brands with ~16k profiles — see
// RELATED_NEWS_COUNT_CONCURRENCY and STATIC_PAGE_GEN_TIMEOUT if that ever returns.
//
// There is deliberately NO cap on how deep pagination goes: every article gets a page.

// One page of related news, plus the grand total so the caller can derive the page
// count from the same response. Throws on failure (after `retry`) so callers decide
// whether a gap is tolerable — a swallowed error here silently drops articles or
// whole pages from the site.
export const getRelatedNewsPage = async (
  institutionId: string,
  page: number,
  size: number
): Promise<{ records: any[]; total: number }> => {
  const url = `${relatedNews}?page=${page}&size=${size}&relatedToInstitution=${institutionId}&order=createdDate,desc`;
  const response = await retry(async () => await get(url), `${institutionId}/related-news-p${page}`);
  const records: any[] = [...response.data.records].sort((x: any, y: any) =>
    sortByDate(x, y, "createdDate", "dsc")
  );
  return { records, total: Number(response.data.pagination.total) || 0 };
};

// Fetch batch size, NOT the display page size. Walking in 10s would cost a firm with
// 5000 articles 500 sequential requests (~110s at the 221ms/request measured on
// stage, holding one of the 16 fan-out slots the whole time); in 1000s it costs 5.
// paginationValidator puts no ceiling on `size`; the real ceiling is OpenSearch's
// index.max_result_window on from+size, which at 1000 lands at 10k articles/firm.
const relatedNewsWalkSize = (): number =>
  Number(process.env.RELATED_NEWS_WALK_SIZE) || 1000;

// Sequential on purpose: the caller in getStaticPaths already fans out over profiles
// with bounded concurrency, so parallelising here would multiply the in-flight load
// on RA API instead of capping it.
const walkRelatedNews = async (institutionId: string): Promise<any[]> => {
  const batchSize = relatedNewsWalkSize();
  const first = await getRelatedNewsPage(institutionId, 1, batchSize);
  const batchCount = Math.ceil(first.total / batchSize);
  const records: any[] = [...first.records];

  // Every batch has to be visited even when one returns nothing: `total` counts
  // pre-filter hits, so a whole batch can filter away while later ones still hold
  // articles. Stopping at the first empty batch would silently lose them.
  for (let page = 2; page <= batchCount; page++) {
    const next = await getRelatedNewsPage(institutionId, page, batchSize);
    records.push(...next.records);
  }

  return records.sort((x: any, y: any) => sortByDate(x, y, "createdDate", "dsc"));
};

// How many articles will actually render. Deliberately NOT cached: getStaticPaths asks
// this for every profile on the brand (~16k on PEI) and holding each firm's records
// would trade a build timeout for a build OOM.
export const getRelatedNewsCount = async (institutionId: string): Promise<number> =>
  (await walkRelatedNews(institutionId)).length;

// Build-time memoisation. The index does not change during a build, and every page
// FILE of the same firm would otherwise re-walk its whole history — a 3-page firm
// costs 3 walks instead of 1. Storing the promise also collapses concurrent callers.
// Unbounded on purpose: a build is short-lived and only firms that actually have
// articles ever land here.
const relatedNewsCache = new Map<string, Promise<any[]>>();

const getCachedRelatedNews = (institutionId: string): Promise<any[]> => {
  const cached = relatedNewsCache.get(institutionId);
  if (cached) {
    return cached;
  }

  // Evict on rejection, otherwise one transient failure would be replayed to every
  // later page file for that firm.
  const pending = walkRelatedNews(institutionId).catch((err) => {
    relatedNewsCache.delete(institutionId);
    throw err;
  });

  relatedNewsCache.set(institutionId, pending);
  return pending;
};

// One listing page's worth of items plus the TRUE total. Same result shape as
// getRelatedNewsPage so callers swap one for the other.
export const getRelatedNewsPageItems = async (
  institutionId: string,
  page: number
): Promise<{ records: any[]; total: number }> => {
  const all = await getCachedRelatedNews(institutionId);
  return { records: sliceRelatedNewsPage(all, page), total: all.length };
};

export const getContacts = async (institutionId: string) => {
  try {
    const url = `${commonRoute}${institutionId}${contactInfo}`;
    const response = await retry(async () => await get(url), `${institutionId}/contacts`);
    const officesRec = response.data.record.offices;
    const officesCount = officesRec.length;
    const officesHQ = officesRec.filter((x: any) => x.isHeadOffice);
    const contactsCount = response.data.record.people.length;

    const contacts = sortContacts(
      response.data.record.people.filter((x: any) => !x.isHeadOffice),
    )
      .slice(0, 4)
      .map((item: any) => {
        item.email = generateRandomChar();
        return item;
      });

    return { officesCount, officesHQ, contacts, contactsCount };
  } catch (ex: any) {
    console.error(`getContacts: institutionId: ${institutionId}, error`, {
      time: new Date().toISOString(),
      url: ex?.config?.url,
      params: ex?.config?.params,
      message: ex?.message,
      code: ex?.code,
      status: ex?.response?.status,
      data: ex?.response?.data,
      stack: ex?.stack,
    });
    return { officesCount: 0, officesHQ: [], contacts: [], contactsCount: 0 };
  }
};

export const getFundsManagedInMarket = async (institutionId: string, partnerStatus: string) => {
  try {
    const fundsManaged = getConditionalStatusWithPartnerStatus("funds-managed", partnerStatus);
    if (!fundsManaged) return { inMarketFundsCount: 0, inMarketFunds: [] };
    const url = `${commonRoute}${institutionId}${fundManaged}?fundsType=inMarket&page=${pageNumber}&size=${maxNumberOfRecords}`;
    const response = await retry(async () => await get(url), `${institutionId}/funds-managed`);
    const inMarketFundsCount = response.data.pagination.total;
    const arrayWithNoDatesRecords: Array<any> = [];
    const arrayWithDatesRecords: Array<any> = [];

    response.data.records.forEach((item: any, index: number) => {
      if (!item.openDateMonth || !item.openDateYear) {
        item.openDate = "-";
        arrayWithDatesRecords.push(item);
        return;
      }

      const date = new Date();
      date.setMonth(item.openDateMonth - 1);
      date.setFullYear(item.openDateYear);
      item.openDate = date;
      if (isNaN(item.openDate.getTime())) {
        return;
      } else {
        arrayWithDatesRecords.push(item);
      }
    });

    let inMarketFunds = arrayWithDatesRecords
      .sort((x: any, y: any) => sortByDate(x, y, "openDate", "dsc"))
      .slice(0, 3);

    inMarketFunds = [...inMarketFunds, ...arrayWithNoDatesRecords];
    inMarketFunds = inMarketFunds.slice(0, 3);
    const finalInMarketFunds = inMarketFunds.map(({ name, openDate, id }: any) => {
      const newItem: Array<FlexibleTableProp> = [
        { text: generateRandomChar(), icon: "lock" },
        { text: generateRandomChar(), icon: "lock" },
        {
          text:
            openDate === "-"
              ? "-"
              : `${getMonthName(openDate.getMonth())} ${openDate.getFullYear()}`,
        },
        { text: generateRandomChar(), icon: "lock" },
      ];

      newItem[0] = {
        text: name,
        link:
          !isBrandToSkip && id
            ? `${URL.basePEIURL}/database/fund-profile/id/${id}/${name}/fund-overview/`
            : null,
      };

      return newItem;
    });

    return { inMarketFundsCount, inMarketFunds: finalInMarketFunds };
  } catch (ex: any) {
    console.error(`getFundsManagedInMarket: institutionId: ${institutionId}, partnerStatus: ${partnerStatus}, error`, {
      time: new Date().toISOString(),
      url: ex?.config?.url,
      params: ex?.config?.params,
      message: ex?.message,
      code: ex?.code,
      status: ex?.response?.status,
      data: ex?.response?.data,
      stack: ex?.stack,
    });

    return { inMarketFundsCount: 0, inMarketFunds: [] };
  }
};

export const getFundsManagedClosed = async (institutionId: string, partnerStatus: string) => {
  try {
    const fundsManaged = getConditionalStatusWithPartnerStatus("funds-managed", partnerStatus);
    if (!fundsManaged) return { closedFundsCount: 0, closedFunds: [] };

    const url = `${commonRoute}${institutionId}${fundManaged}?fundsType=closed&page=${pageNumber}&size=${maxNumberOfRecords}`;
    const response = await retry(
      async () => await get(url),
      `${institutionId}/fund-managed-closed`
    );
    const closedFundsCount = response.data.pagination.total;
    const arrayWithNoDatesRecords: Array<any> = [];
    const arrayWithDatesRecords: Array<any> = [];

    response.data.records.forEach((item: any, index: number) => {
      if (!item.closeDateYear || !item.closeDateMonth) {
        item.closeDate = "-";
        arrayWithNoDatesRecords.push(item);
        return;
      }

      const date = new Date();
      date.setMonth(item.closeDateMonth - 1);
      date.setFullYear(item.closeDateYear);
      item.closeDate = date;
      if (isNaN(item.closeDate.getTime())) {
        return;
      } else {
        arrayWithDatesRecords.push(item);
      }
    });

    let closedFunds = arrayWithDatesRecords
      .sort((x: any, y: any) => sortByDate(x, y, "closeDate", "dsc"))
      .slice(0, 3);

    closedFunds = [...closedFunds, ...arrayWithNoDatesRecords];
    closedFunds = closedFunds.slice(0, 3);
    const finalClosedFunds = closedFunds.map(({ name, closeDate, id }: any) => {
      const newItem: Array<FlexibleTableProp> = [
        { text: generateRandomChar(), icon: "lock" },
        { text: generateRandomChar(), icon: "lock" },
        {
          text:
            closeDate === "-"
              ? "-"
              : `${getMonthName(closeDate.getMonth())} ${closeDate.getFullYear()}`,
        },
        { text: generateRandomChar(), icon: "lock" },
      ];

      newItem[0] = {
        text: name,
        link:
          !isBrandToSkip && id
            ? `${URL.basePEIURL}/database/fund-profile/id/${id}/${name}/fund-overview/`
            : null,
      };

      return newItem;
    });

    return { closedFundsCount, closedFunds: finalClosedFunds };
  } catch (ex: any) {
    console.error(`getFundsManagedClosed: institutionId: ${institutionId}, partnerStatus: ${partnerStatus}, error`, {
      time: new Date().toISOString(),
      url: ex?.config?.url,
      params: ex?.config?.params,
      message: ex?.message,
      code: ex?.code,
      status: ex?.response?.status,
      data: ex?.response?.data,
      stack: ex?.stack,
    });

    return { closedFundsCount: 0, closedFunds: [] };
  }
};

export const getInvestmentAppetite = async (institutionId: string, partnerStatus: string) => {
  const overviewData = {
    description: { background: { __html: "No background data exists" } },
    email: "",
    website: "",
    hQ: {
      address: {
        city: {
          name: "",
        },
        country: {
          name: "",
        },
      },
    },
  };
  try {
    const checkInvestmentAppetite = getConditionalStatusWithPartnerStatus(
      "investment-appetite",
      partnerStatus
    );
    const investmentAppetite: investmentAppetiteType = {
      strategy: {} as investmentAppetiteItemType,
      region: {} as investmentAppetiteItemType,
      sector: {} as investmentAppetiteItemType,
    };

    const url = `${commonRoute}${institutionId}`;
    const response = await retry(
      async () => await get(url),
      `${institutionId}/investment-appetite`
    );
    const record = response.data.record;
    overviewData.description = record?.backgrounds[0]
      ? {
        background: {
          __html: record?.backgrounds[0]?.background
            ?.replaceAll("\r\n", "<br/>")
            ?.replaceAll("\n", "<br/>")
            ?.replaceAll("\t", "&emsp;"),
        },
      }
      : overviewData.description;
    overviewData.email = record.email ? record.email : overviewData.email;
    overviewData.website = record.website ? record.website : overviewData.website;
    const filterHQ = record.branches.filter((x: any) => x.isHeadOffice)[0];
    overviewData.hQ = filterHQ ? filterHQ : overviewData.hQ;
    const investmentRec = record.institutionFocuses;
    investmentRec.map((investment: any) => {
      if (investment.forPartnerStatus.name === "LP") {
        investmentAppetite.region = investment.regionFocus
          ? investment.regionFocus
            .sort((x: investmentAppetiteItemType, y: investmentAppetiteItemType) =>
              sortByAlphabetOrder(x, y, "name", "PE - ")
            )
            .shift()
          : {};
        investmentAppetite.strategy = investment.strategyFocus
          ? investment.strategyFocus
            .sort((x: investmentAppetiteItemType, y: investmentAppetiteItemType) =>
              sortByAlphabetOrder(x, y, "name", "PE - ")
            )
            .shift()
          : {};
        investmentAppetite.sector = investment.sectorFocus
          ? investment.sectorFocus
            .sort((x: investmentAppetiteItemType, y: investmentAppetiteItemType) =>
              sortByAlphabetOrder(x, y, "name", "PE - ")
            )
            .shift()
          : {};
      }
    });

    if (!checkInvestmentAppetite)
      return { overviewData, investmentAppetite: {}, hasApetiteRecord: false };
    const hasApetiteRecord =
      investmentAppetite.region || investmentAppetite.strategy || investmentAppetite.strategy;
    return {
      investmentAppetite,
      hasApetiteRecord: JSON.stringify(hasApetiteRecord) !== "{}",
      overviewData,
    };
  } catch (ex: any) {
    console.error(`getInvestmentAppetite: institutionId: ${institutionId}, partnerStatus: ${partnerStatus}, error`, {
      time: new Date().toISOString(),
      url: ex?.config?.url,
      params: ex?.config?.params,
      message: ex?.message,
      code: ex?.code,
      status: ex?.response?.status,
      data: ex?.response?.data,
      stack: ex?.stack,
    });

    return { overviewData, investmentAppetite: {}, hasApetiteRecord: false };
  }
};

export const getFundCommitments = async (institutionId: string, partnerStatus: string) => {
  try {
    const checkFundCommitment = getConditionalStatusWithPartnerStatus(
      "fund-commitment",
      partnerStatus
    );
    if (!checkFundCommitment) return { fundCommitmentsCount: 0, fundCommitments: [] };
    const fundRecord: fundCommitmentType[] = [];
    const url = `${commonRoute}${institutionId}${fundCommitment}?page=1&size=${maxNumberOfRecords}`;
    const response = await retry(async () => await get(url), `${institutionId}/fund-commitments`);
    const fundCommitments = response.data.records;
    const fundCommitmentsCount = fundCommitments.length;
    //  first sorting with in infund itself with vintage year
    fundCommitments.map((x: any) =>
      x.inFund.sort(
        (a: any, b: any) => new Date(b.vintageYear).valueOf() - new Date(a.vintageYear).valueOf()
      )
    );
    //  secondly sorting with infund vintage year as compare to other records
    fundCommitments.sort(
      (a: any, b: any) =>
        new Date(b.inFund[0].vintageYear).valueOf() - new Date(a.inFund[0].vintageYear).valueOf()
    );
    fundCommitments.slice(0, 3).map((item: any, index: number) => {
      const fundLocal: fundCommitmentType = {} as fundCommitmentType;
      fundLocal.fund = item.inFund[0].name;
      fundLocal.id = item.inFund[0].id;
      fundLocal.manager = generateRandomChar();
      fundLocal.commitmentDate = item.commitmentDate
        ? `${getMonthByName(item.commitmentDate)} ${new Date(item.commitmentDate).getFullYear()}`
        : "-";

      fundLocal.commitmentSize =
        index !== 0
          ? generateRandomChar()
          : !item.commitmentSize
            ? "-"
            : !item.commitmentSize.usdValue
              ? "-"
              : numberFormat(item.commitmentSize.usdValue);
      fundRecord.push(fundLocal);
    });
    return { fundCommitmentsCount, fundCommitments: fundRecord };
  } catch (ex: any) {
    console.error(`getFundCommitments: institutionId: ${institutionId}, partnerStatus: ${partnerStatus}, error`, {
      time: new Date().toISOString(),
      url: ex?.config?.url,
      params: ex?.config?.params,
      message: ex?.message,
      code: ex?.code,
      status: ex?.response?.status,
      data: ex?.response?.data,
      stack: ex?.stack,
    });

    return { fundCommitmentsCount: 0, fundCommitments: [] };
  }
};

export const getProfilesByRegionAndPartnerStatus: any = async (
  partnerStatus: string,
  regionIndex = -1,
  page = 1,
  // 500-doc pages of LP institutions are too heavy for the RA API to serve
  // within any timeout (WEB-30081) — keep pages small; ops can tune via env.
  size = Number(process.env.PROFILE_FETCH_PAGE_SIZE) || 50,
  _profileRecords: any[] = [],
  _regions: any[] = []
) => {
  try {
    if (regionIndex === -1) {
      //get all the regions available
      const response = await retry(async () => await get("/regions"), `${partnerStatus}/regions`);
      _regions = response.data.records;
      regionIndex = 0;
    }
    const url = `/institutions?partnerStatus=${partnerStatus}&page=${page}&size=${size}&locRegion=${_regions[regionIndex]["id"]}`;
    const response = await retry(async () => await get(url), `${partnerStatus}/profiles`);
    const pageCount = response.data.pagination.pageCount;
    _profileRecords = [..._profileRecords, ...response.data.records];

    if (page === pageCount) {
      regionIndex++;
      page = 0;
    }
    if (regionIndex === _regions.length) {

      const profileRec = _profileRecords.map((pr: any) => {
        return {
          Institution: pr.name,
          ID: pr.id,
          PartnerStatus: partnerStatus,
        } as instituteProps;
      });

      return await readAndUpdateJsonFile(profileRec, partnerStatus);

    } else {
      return getProfilesByRegionAndPartnerStatus(partnerStatus, regionIndex, page + 1, size, _profileRecords, _regions);
    }
  } catch (ex: any) {
    console.error(`getProfilesByRegionAndPartnerStatus: Server failed while fetching ${partnerStatus} profiles`, {
      time: new Date().toISOString(),
      url: ex?.config?.url,
      params: ex?.config?.params,
      message: ex?.message,
      code: ex?.code,
      status: ex?.response?.status,
      data: ex?.response?.data,
      stack: ex?.stack,
    });
    return false;
  }
}

export const getProfiles: any = async (partnerStatus: string, page = 1, size = 500, _profileRecords: any[] = []) => {
  try {
    const url = `/institutions?partnerStatus=${partnerStatus}&page=${page}&size=${size}`;
    const response = await retry(async () => await get(url), `${partnerStatus}/profiles`);
    const pageCount = response.data.pagination.pageCount;
    _profileRecords = [..._profileRecords, ...response.data.records];
    if (page === pageCount) {
      const profileRec = _profileRecords.map((pr: any) => {
        return {
          Institution: pr.name,
          ID: pr.id,
          PartnerStatus: partnerStatus,
        } as instituteProps;
      });
      return await readAndUpdateJsonFile(profileRec, partnerStatus);
    } else {
      return getProfiles(partnerStatus, page + 1, size, _profileRecords);
    }
  } catch (ex: any) {
    console.error(`getProfiles: Server failed while fetching ${partnerStatus} profiles`, {
      time: new Date().toISOString(),
      url: ex?.config?.url,
      params: ex?.config?.params,
      message: ex?.message,
      code: ex?.code,
      status: ex?.response?.status,
      data: ex?.response?.data,
      stack: ex?.stack,
    });
    return false;
  }
};

const readAndUpdateJsonFile = async (fetchedData: instituteProps[], partnerStatus: string) => {
  return new Promise(async (resolve, reject) => {
    await fs.readFile(jsonFilePath, "utf8", async (err: any, data: any) => {
      if (err) {
        console.error(err);
        reject(err);
        return;
      }

      const jsonFileData = JSON.parse(data);
      jsonFileData.Profiles.length > 0 &&
        jsonFileData.Profiles.map((jdata: instituteProps) => {
          const foundRec = fetchedData.find((x: instituteProps) => x.ID === jdata.ID);
          if (foundRec) {
            if (foundRec.PartnerStatus !== jdata.PartnerStatus) jdata.PartnerStatus = "LP/GP";
            foundRec.Duplicated = true;
          }
        });
      const filteredArray = fetchedData.filter((obj: instituteProps) => !(obj.Duplicated === true));
      const updatedProfileRecords = [...jsonFileData["Profiles"], ...filteredArray];
      const profileStructure = {
        Profiles: updatedProfileRecords,
      };
      // Convert the updated JSON object back to a string
      const updatedData = JSON.stringify(profileStructure, null, 2);

      // Write the updated data back to the file
      return await fs.writeFile(jsonFilePath, updatedData, "utf8", (err: any) => {
        if (err) {
          console.log(
            `There is something went wrong while updating ${partnerStatus} profiles data`
          );
          console.error(err);
          reject(false);
        }
        console.log(
          `Total ${fetchedData.length} of ${partnerStatus} profiles data updated successfully`
        );
        resolve(true);
      });
    });
  });
};
